import UserAvatar, { getPresetAvatarSrc } from '@/components/avatar/UserAvatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { AvatarPreset, useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';
import { cn } from '@/lib/utils';
import { Upload, User } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const CROP_BOX_SIZE = 220;
const OUTPUT_SIZE = 256;
const MAX_UPLOAD_BYTES = 250 * 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const ProfileAvatarSelector: React.FC<{ className?: string }> = ({ className }) => {
  const { api } = useApiClient();
  const { user, refreshAuthSnapshot } = useAuth();

  const [presets, setPresets] = useState<AvatarPreset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(true);
  const [savingSelection, setSavingSelection] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);

  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [cropShape, setCropShape] = useState<'square' | 'circle'>('square');

  const scaleAtZoom = (image: HTMLImageElement, zoomLevel: number): number => {
    const fitScale = Math.min(
      CROP_BOX_SIZE / image.width,
      CROP_BOX_SIZE / image.height,
    );
    return fitScale * zoomLevel;
  };

  const clampOffset = (
    nextOffset: { x: number; y: number },
    image: HTMLImageElement | null,
    zoomLevel: number,
  ): { x: number; y: number } => {
    if (!image) return { x: 0, y: 0 };
    const scaledWidth = image.width * scaleAtZoom(image, zoomLevel);
    const scaledHeight = image.height * scaleAtZoom(image, zoomLevel);
    const maxX = Math.max(0, (scaledWidth - CROP_BOX_SIZE) / 2);
    const maxY = Math.max(0, (scaledHeight - CROP_BOX_SIZE) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, nextOffset.x)),
      y: Math.min(maxY, Math.max(-maxY, nextOffset.y)),
    };
  };

  useEffect(() => {
    let mounted = true;
    const loadPresets = async () => {
      try {
        const presetList = await api.getAvatarPresets();
        if (!mounted) return;
        setPresets(presetList);
      } catch (error) {
        toast.error('Failed to load avatar options');
      } finally {
        if (mounted) setLoadingPresets(false);
      }
    };

    loadPresets();
    return () => {
      mounted = false;
    };
  }, [api]);

  useEffect(() => {
    if (!imageSrc) {
      setLoadedImage(null);
      return;
    }

    const img = new Image();
    img.onload = () => setLoadedImage(img);
    img.src = imageSrc;
  }, [imageSrc]);

  const hasUploadDraft = useMemo(() => !!imageSrc && !!loadedImage, [imageSrc, loadedImage]);

  const onChooseFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setZoom(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      setCropShape('square');
    };
    reader.onerror = () => toast.error('Failed to read image');
    reader.readAsDataURL(file);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!hasUploadDraft) return;
    e.preventDefault();
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startX: offset.x,
      startY: offset.y,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setOffset(clampOffset({
      x: dragStartRef.current.startX + dx,
      y: dragStartRef.current.startY + dy,
    }, loadedImage, zoom));
  };

  const handlePointerUp = () => {
    setDragging(false);
    dragStartRef.current = null;
  };

  const renderCroppedBlob = async (): Promise<Blob> => {
    if (!loadedImage) {
      throw new Error('No image selected');
    }

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to prepare image canvas');
    }

    const totalScale = scaleAtZoom(loadedImage, zoom);
    const ratio = OUTPUT_SIZE / CROP_BOX_SIZE;

    if (cropShape === 'circle') {
      ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      ctx.save();
      ctx.beginPath();
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.translate(OUTPUT_SIZE / 2 + offset.x * ratio, OUTPUT_SIZE / 2 + offset.y * ratio);
      ctx.scale(totalScale * ratio, totalScale * ratio);
      ctx.drawImage(loadedImage, -loadedImage.width / 2, -loadedImage.height / 2);
      ctx.restore();

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob || blob.size > MAX_UPLOAD_BYTES) {
        throw new Error('Image is too large. Try reducing zoom.');
      }
      return blob;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.translate(OUTPUT_SIZE / 2 + offset.x * ratio, OUTPUT_SIZE / 2 + offset.y * ratio);
    ctx.scale(totalScale * ratio, totalScale * ratio);
    ctx.drawImage(loadedImage, -loadedImage.width / 2, -loadedImage.height / 2);

    const toBlob = (quality: number) =>
      new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

    let quality = 0.9;
    let blob = await toBlob(quality);
    while (blob && blob.size > MAX_UPLOAD_BYTES && quality >= 0.55) {
      quality -= 0.1;
      blob = await toBlob(quality);
    }

    if (!blob || blob.size > MAX_UPLOAD_BYTES) {
      throw new Error('Image is too large. Try reducing zoom.');
    }

    return blob;
  };

  const selectNone = async () => {
    try {
      setSavingSelection(true);
      await api.selectAvatar('none');
      await refreshAuthSnapshot();
      setIsEditorOpen(false);
      toast.success('Avatar removed');
    } catch (error: any) {
      toast.error(getUserErrorMessage(error, 'Unable to update avatar right now. Please try again later.'));
    } finally {
      setSavingSelection(false);
    }
  };

  const selectPreset = async (presetId: string) => {
    try {
      setSavingSelection(true);
      await api.selectAvatar('preset', presetId);
      await refreshAuthSnapshot();
      setIsEditorOpen(false);
      toast.success('Avatar updated');
    } catch (error: any) {
      toast.error(getUserErrorMessage(error, 'Unable to update avatar right now. Please try again later.'));
    } finally {
      setSavingSelection(false);
    }
  };

  const uploadAvatar = async () => {
    try {
      setUploading(true);
      const blob = await renderCroppedBlob();
      const formData = new FormData();
      formData.append('image', blob, cropShape === 'circle' ? 'avatar.png' : 'avatar.jpg');
      await api.uploadAvatar(formData);
      await refreshAuthSnapshot();
      setImageSrc(null);
      setLoadedImage(null);
      setOffset({ x: 0, y: 0 });
      setZoom(MIN_ZOOM);
      setIsEditorOpen(false);
      toast.success('Avatar uploaded');
    } catch (error: any) {
      toast.error(getUserErrorMessage(error, 'Unable to upload avatar right now. Please try again later.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      <button
        type="button"
        onClick={() => setIsEditorOpen((prev) => !prev)}
        className="flex items-center gap-3 rounded-lg border bg-white px-3 py-2 transition hover:border-blue-300"
      >
        <UserAvatar user={user} className="h-16 w-16 border-0" />
        <div className="text-left">
          <p className="text-sm font-medium text-gray-900">Profile picture</p>
          <p className="text-xs text-gray-600">{isEditorOpen ? 'Hide editor' : 'Click to edit avatar'}</p>
        </div>
      </button>

      {isEditorOpen && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Edit Avatar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <UserAvatar user={user} className="h-20 w-20 border-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Current avatar</p>
                <p className="text-xs text-gray-600">Choose none, a preset, or upload your own photo.</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">None</Label>
                <Button type="button" variant="outline" size="sm" onClick={selectNone} disabled={savingSelection || uploading}>
                  <User className="mr-2 h-4 w-4" />
                  Use silhouette
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Preset avatars</Label>
              {loadingPresets ? (
                <p className="text-xs text-gray-500">Loading presets...</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {presets.map((preset) => {
                    const active = user?.avatar_mode === 'preset' && user?.avatar_preset === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => selectPreset(preset.id)}
                        disabled={savingSelection || uploading}
                        className={cn(
                          'rounded-md border transition-all duration-150 hover:border-blue-400 disabled:opacity-60 bg-white overflow-hidden',
                          active
                            ? 'border-blue-600 ring-2 ring-blue-500 scale-110 z-10'
                            : 'border-gray-200',
                        )}
                      >
                        <img
                          src={getPresetAvatarSrc(preset.id)}
                          alt={`Avatar ${preset.id}`}
                          className="h-20 w-full sm:h-24 object-contain"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-3 border-t pt-4">
              <Label htmlFor="avatar-upload" className="text-sm font-medium">Upload image</Label>
              <Input id="avatar-upload" type="file" accept="image/*" onChange={onChooseFile} />
              {hasUploadDraft && loadedImage && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={cropShape === 'square' ? 'default' : 'outline'}
                      onClick={() => setCropShape('square')}
                    >
                      Square
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={cropShape === 'circle' ? 'default' : 'outline'}
                      onClick={() => setCropShape('circle')}
                    >
                      Circle
                    </Button>
                  </div>
                  <div className="rounded-lg border bg-gray-50 p-3">
                    <div className="mx-auto flex items-center justify-center gap-3">
                      <div
                        className={cn(
                          'relative overflow-hidden border-2 border-gray-300 touch-none bg-white',
                          cropShape === 'circle' ? 'rounded-full' : 'rounded-md',
                        )}
                        style={{ width: CROP_BOX_SIZE, height: CROP_BOX_SIZE, touchAction: 'none' }}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                      >
                        <img
                          src={imageSrc || ''}
                          alt="Avatar crop preview"
                          className="absolute left-1/2 top-1/2 max-w-none select-none"
                          draggable={false}
                          style={{
                            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scaleAtZoom(loadedImage, zoom)})`,
                            transformOrigin: 'center center',
                            cursor: dragging ? 'grabbing' : 'grab',
                            width: loadedImage.width,
                            height: loadedImage.height,
                          }}
                        />
                      </div>
                      <div className="flex h-[220px] flex-col items-center justify-between py-1">
                        <span className="text-[11px] text-gray-600">Zoom</span>
                        <input
                          type="range"
                          min={MIN_ZOOM}
                          max={MAX_ZOOM}
                          step={0.01}
                          value={zoom}
                          onChange={(e) => {
                            const nextZoom = Number(e.target.value);
                            setZoom(nextZoom);
                            setOffset((prev) => clampOffset(prev, loadedImage, nextZoom));
                          }}
                          className="h-[170px] w-5 cursor-pointer"
                          style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                        />
                        <span className="text-[11px] text-gray-500">{Math.round(zoom * 100)}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setImageSrc(null);
                        setLoadedImage(null);
                        setZoom(MIN_ZOOM);
                        setOffset({ x: 0, y: 0 });
                        setCropShape('square');
                      }}
                      disabled={uploading}
                    >
                      Cancel
                    </Button>
                    <Button type="button" onClick={uploadAvatar} disabled={uploading || savingSelection}>
                      <Upload className="mr-2 h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Upload avatar'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Drag to reposition and use zoom before uploading. Uploads are capped to a small, private file.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfileAvatarSelector;
